import {
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Table } from '@tanstack/react-table';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { AppTableRef } from '../../../app-table/types/table-props';

export type ExportColumn = {
  id: string;
  label: string;
};

const getExportColumns = <TData>(table: Table<TData>): ExportColumn[] => {
  const columnOrder = table.getState().columnOrder;
  const orderMap = new Map(columnOrder.map((id, index) => [id, index]));

  const columns = table
    .getAllLeafColumns()
    .filter(
      (column) =>
        column.id !== 'actions' &&
        column.id !== 'select' &&
        !column.columnDef.meta?.isServiceColumn,
    )
    .sort((a, b) => {
      const aIndex = orderMap.get(a.id);
      const bIndex = orderMap.get(b.id);
      return (
        Number(aIndex === undefined) - Number(bIndex === undefined) ||
        (aIndex ?? 0) - (bIndex ?? 0)
      );
    });

  return columns.map((column) => ({
    id: column.id,
    label: column.columnDef.meta?.name ?? column.id,
  }));
};

const getExportRows = <TData>(
  table: Table<TData>,
  columnIds: string[],
): Record<string, unknown>[] => {
  return table.getRowModel().rows.map((row) => {
    return columnIds.reduce(
      (acc, columnId) => {
        acc[columnId] = row.getValue(columnId);
        return acc;
      },
      {} as Record<string, unknown>,
    );
  });
};

export interface UseAppTableExportDialogParams<TData> {
  tableRef: RefObject<AppTableRef<TData>>;
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void;
  onPdf?: (columns: string[], rows: Partial<TData>[]) => void;
}

export const useAppTableExportDialog = <TData>({
  tableRef,
  onExcel,
  onPdf,
}: UseAppTableExportDialogParams<TData>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [columns, setColumns] = useState<ExportColumn[]>([]);
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasExportHandlers = !!onExcel || !!onPdf;

  useEffect(() => {
    if (!isOpen) return;
    const table = tableRef.current?.getTable();
    if (!table) return;
    const nextColumns = getExportColumns(table);
    const nextIds = nextColumns.map((column) => column.id);
    setColumns(nextColumns);
    setSelectedColumnIds((prev) => {
      if (!prev.length) return nextIds;
      const prevSet = new Set(prev);
      const kept = nextIds.filter((id) => prevSet.has(id));
      const missing = nextIds.filter((id) => !prevSet.has(id));
      return kept.concat(missing);
    });
  }, [isOpen, tableRef]);

  const handleOpenChange = useCallback((value: boolean) => {
    setIsOpen(value);
  }, []);

  const orderedColumnIds = useMemo(
    () => columns.map((column) => column.id),
    [columns],
  );

  const selectedColumnSet = useMemo(
    () => new Set(selectedColumnIds),
    [selectedColumnIds],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleToggleColumn = useCallback(
    (id: string, value: boolean) => {
      setSelectedColumnIds((prev) => {
        const next = new Set(prev);
        if (value) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return orderedColumnIds.filter((columnId) => next.has(columnId));
      });
    },
    [orderedColumnIds],
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumns((prev) => {
      const oldIndex = prev.findIndex((column) => column.id === active.id);
      const newIndex = prev.findIndex((column) => column.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      setSelectedColumnIds((selected) => {
        const selectedSet = new Set(selected);
        return next
          .map((column) => column.id)
          .filter((id) => selectedSet.has(id));
      });
      return next;
    });
  }, []);

  const exportExcel = useCallback(() => {
    if (!onExcel || !selectedColumnIds.length || isSubmitting) return;
    const table = tableRef.current?.getTable();
    if (!table) return;

    const rows = getExportRows(table, selectedColumnIds);
    setIsSubmitting(true);
    Promise.resolve(onExcel(selectedColumnIds, rows as Partial<TData>[]))
      .then(() => {
        setIsOpen(false);
      })
      .finally(() => setIsSubmitting(false));
  }, [isSubmitting, onExcel, selectedColumnIds, tableRef]);

  const exportPdf = useCallback(() => {
    if (!onPdf || !selectedColumnIds.length || isSubmitting) return;
    const table = tableRef.current?.getTable();
    if (!table) return;

    const rows = getExportRows(table, selectedColumnIds);
    setIsSubmitting(true);
    Promise.resolve(onPdf(selectedColumnIds, rows as Partial<TData>[]))
      .then(() => {
        setIsOpen(false);
      })
      .finally(() => setIsSubmitting(false));
  }, [isSubmitting, onPdf, selectedColumnIds, tableRef]);

  return {
    hasExportHandlers,

    isOpen,
    handleOpenChange,

    columns,
    selectedColumnIds,
    selectedColumnSet,

    sensors,
    handleToggleColumn,
    handleDragEnd,

    isSubmitting,
    exportExcel,
    exportPdf,
  };
};
